import { Button, Layout, message, Modal, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import ActionHeader from '../../../components/actionHeader';
import Page from '../../../components/page';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store';
import { StyledContent, StyledMenu } from './styles';
import {
  fetchLinkedMachines,
  fetchTemplateById,
  resetTemplate,
  selectDraftTemplate,
  selectIsFetchingTemplates,
} from '../../../slices/templates';
import { fetchMachines } from '../../../slices/machines';
import { copyTemplate, deleteTemplates } from '../../../utils/httpRequest';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useTheme } from 'styled-components';
import { resetSolutionGrids } from '../../../slices/solutionGrids';

export const menus = [
  {
    key: '',
    label: 'Details',
  },
  {
    key: 'solutionGrid',
    label: 'Solution Grid',
  },
  {
    key: 'issue',
    label: 'Issue',
  },
  {
    key: 'cause',
    label: 'Cause',
  },
  {
    key: 'routine',
    label: 'Routine',
  },
  {
    key: 'resourceLibrary',
    label: 'Resource Library',
  },
];

const TemplateDetailsContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id = '' } = useParams();
  const theme = useTheme();
  const [messageApi, contextHolder] = message.useMessage();
  const [modal, modalContextHolder] = Modal.useModal();

  const template = useSelector(selectDraftTemplate);
  const isFetching = useSelector(selectIsFetchingTemplates);

  const { pathname } = location;

  const selectedKeys = pathname.split('/').slice(4);

  const [defaultOpenKeys, setDefaultOpenKeys] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    try {
      setShowDeleteModal(false);
      setLoading(true);
      await deleteTemplates(id);
      navigate('/knowledge/template');
    } catch (err) {
      console.log(err);
      messageApi.error('Delete Failed!');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      setLoading(true);
      const res = await copyTemplate(id);
      modal.confirm({
        title: 'Template Copy Successfully!',
        icon: (
          <CheckCircleOutlined style={{ color: theme.antd.colorPrimary }} />
        ),
        content: 'Go to copied template?',
        okText: 'Ok',
        onOk() {
          navigate(`/knowledge/template/${res.templateID}`);
        },
        cancelText: 'Stay Here',
      });
    } catch (err) {
      console.log(err);
      messageApi.error('Copy Failed!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(resetTemplate());
    dispatch(resetSolutionGrids());
    dispatch(fetchTemplateById(id));
    dispatch(fetchLinkedMachines(id));
    dispatch(fetchMachines());
  }, [id]);

  return (
    <Page
      backgroundColor="#f6f6f6"
      gap={0}
      header={
        <ActionHeader
          title={template?.name}
          showBack
          onBack={() => navigate(-1)}
          endSlot={
            <>
              <Button
                type="primary"
                danger
                ghost
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </Button>
              <Button type="primary" ghost onClick={handleCopy}>
                Copy
              </Button>
            </>
          }
        />
      }
    >
      {contextHolder}
      {modalContextHolder}
      <Spin spinning={isFetching} fullscreen />
      <Layout style={{ height: '100%' }}>
        <Layout.Sider style={{ backgroundColor: '#f7f7f7' }} width={144}>
          <StyledMenu
            selectedKeys={selectedKeys[0] ? selectedKeys : ['']}
            defaultOpenKeys={defaultOpenKeys}
            theme="light"
            items={menus}
            onClick={({ key, keyPath }) => {
              navigate(key, { replace: true });
              setDefaultOpenKeys(keyPath);
            }}
          />
        </Layout.Sider>
        <StyledContent>
          <Outlet />
        </StyledContent>
        <Modal
          title={`Delete ${template?.name}`}
          closable={{ 'aria-label': 'Custom Close Button' }}
          open={showDeleteModal}
          onOk={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
        >
          <p>Are you sure to delete {template?.name}?</p>
        </Modal>
        <Spin fullscreen spinning={loading}>
          Deleting
        </Spin>
      </Layout>
    </Page>
  );
};

export default TemplateDetailsContainer;
